// <s-player-menu> adds Subtitles and Quality buttons to one episode's uploaded video.
// Quality: switches between the MP4 sizes Shopify made for the video (data-qualities), keeping the time and play state.
// Subtitles: looks for .vtt or .srt files uploaded to Content > Files (data-subs), only once the episode first plays;
// the CC button appears only when a file exists. Choices are remembered for the visit (sessionStorage).
const QUALITY_KEY = 'horhy-quality';
const SUBS_KEY = 'horhy-subs';

class SPlayerMenu extends HTMLElement {
  #ac = null;
  #video = null;
  #qualities = [];
  #subs = [];
  #subsChecked = false;

  connectedCallback() {
    this.#video = this.closest('[data-episode]')?.querySelector('video');
    if (!this.#video) return;
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    try { this.#qualities = JSON.parse(this.dataset.qualities || '[]'); } catch { this.#qualities = []; }
    try { this.#subs = JSON.parse(this.dataset.subs || '[]'); } catch { this.#subs = []; }

    // No "Save video as…" on right-click.
    this.#video.addEventListener('contextmenu', (event) => event.preventDefault(), { signal });
    this.#markQuality('auto');
    // Look for the other sizes and subtitle files only once the episode first plays.
    this.#video.addEventListener('play', () => {
      this.#discoverQualities();
      this.#checkSubs();
    }, { signal, once: true });

    this.addEventListener('command', (event) => {
      const value = event.source?.dataset.value;
      if (event.command === '--toggle-quality') this.#toggle('[data-quality-panel]');
      else if (event.command === '--toggle-subs') this.#toggle('[data-subs-panel]');
      else if (event.command === '--set-quality') { this.#setQuality(value, true); this.#closeAll(); }
      else if (event.command === '--set-subs') { this.#setSubs(value); this.#closeAll(); }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#closeAll();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeAll();
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #toggle(selector) {
    const panel = this.querySelector(selector);
    if (!panel) return;
    const open = panel.hidden;
    this.#closeAll();
    panel.hidden = !open;
    const toggle = this.querySelector(selector === '[data-quality-panel]' ? '[data-quality-toggle]' : '[data-subs-toggle]');
    toggle?.setAttribute('aria-expanded', String(open));
  }

  #closeAll() {
    this.querySelectorAll('[data-quality-panel], [data-subs-panel]').forEach((panel) => { panel.hidden = true; });
    this.querySelectorAll('[aria-expanded]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  }

  #markQuality(value) {
    this.querySelectorAll('[command="--set-quality"]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === value));
    });
    const label = this.querySelector('[data-quality-label]');
    if (label) label.textContent = value === 'auto' ? (this.dataset.autoLabel || '') : `${value}p`;
  }

  // Swaps the video file but keeps the viewer's place and play/pause state.
  #setQuality(value, remember) {
    const video = this.#video;
    // Adaptive stream: Auto lets it pick the best quality the connection can keep up with; a size locks to that file.
    const stream = video.closest('s-stream');
    if (stream?.useAuto) {
      if (value === 'auto') {
        stream.useAuto();
      } else {
        const choice = this.#qualities.find((q) => String(q.height) === String(value));
        if (!choice) return;
        stream.useFile(choice.url);
      }
      this.#markQuality(value);
      if (remember) sessionStorage.setItem(QUALITY_KEY, value);
      return;
    }
    const time = video.currentTime || 0;
    const wasPlaying = !video.paused;
    if (value === 'auto') {
      video.removeAttribute('src');
    } else {
      const choice = this.#qualities.find((q) => String(q.height) === String(value));
      if (!choice) return;
      video.src = choice.url;
    }
    video.addEventListener('loadedmetadata', () => {
      if (time) video.currentTime = time;
      if (wasPlaying) video.play().catch(() => {});
    }, { once: true });
    video.load();
    this.#markQuality(value);
    if (remember) sessionStorage.setItem(QUALITY_KEY, value);
  }

  // The page only receives the largest MP4. Shopify names its other sizes in a fixed pattern
  // (e.g. ".HD-720p-4.5Mbps-123.mp4"), so try the likely names and keep the ones that exist.
  async #discoverQualities() {
    const base = this.#qualities[0]?.url;
    const match = base?.match(/^(.*)\.(?:HD|SD)-(\d+)p-([\d.]+)Mbps-(\d+)\.mp4(\?.*)?$/);
    if (!match) return;
    const [, prefix, , rate, id] = match;
    const tiers = [
      { 1080: '7.2', 720: '4.5', 480: '1.5' },
      { 1080: '2.5', 720: '1.6', 480: '0.9' },
    ];
    const ordered = tiers.sort((a, b) => Number(Object.values(b).includes(rate)) - Number(Object.values(a).includes(rate)));
    const found = new Map();
    for (const height of [1080, 720, 480]) {
      for (const tier of ordered) {
        const url = `${prefix}.${height >= 720 ? 'HD' : 'SD'}-${height}p-${tier[height]}Mbps-${id}.mp4`;
        if (url.split('?')[0] === base.split('?')[0] || await this.#exists(url)) {
          found.set(height, url);
          break;
        }
      }
    }
    this.#qualities = [...found].map(([height, url]) => ({ height, url }));
    this.querySelectorAll('[command="--set-quality"]').forEach((button) => {
      if (button.dataset.value !== 'auto') button.hidden = !found.has(Number(button.dataset.value));
    });
    const toggle = this.querySelector('[data-quality-toggle]');
    if (toggle) toggle.hidden = this.#qualities.length < 2;
    const saved = sessionStorage.getItem(QUALITY_KEY);
    if (saved && saved !== 'auto' && found.has(Number(saved))) this.#setQuality(saved, false);
  }

  // Loads just the video's first details in a hidden player: works on any video server, no download of the whole file.
  #exists(url) {
    return new Promise((resolve) => {
      const probe = document.createElement('video');
      let settled = false;
      const done = (ok) => {
        if (settled) return;
        settled = true;
        probe.removeAttribute('src');
        probe.load();
        resolve(ok);
      };
      probe.preload = 'metadata';
      probe.muted = true;
      probe.addEventListener('loadedmetadata', () => done(true), { once: true });
      probe.addEventListener('error', () => done(false), { once: true });
      setTimeout(() => done(false), 8000);
      probe.src = url;
    });
  }

  async #checkSubs() {
    if (this.#subsChecked || this.#subs.length === 0) return;
    this.#subsChecked = true;
    const found = [];
    for (const sub of this.#subs) {
      const text = await this.#loadSub(sub);
      if (text) found.push({ ...sub, text });
    }
    if (found.length === 0) return;

    for (const sub of found) {
      const track = document.createElement('track');
      track.kind = 'subtitles';
      track.srclang = sub.lang;
      track.label = sub.label;
      track.src = URL.createObjectURL(new Blob([sub.text], { type: 'text/vtt' }));
      this.#video.append(track);
    }
    this.querySelectorAll('[command="--set-subs"]').forEach((button) => {
      if (button.dataset.value === 'off') return;
      button.hidden = !found.some((sub) => sub.lang === button.dataset.value);
    });
    const toggle = this.querySelector('[data-subs-toggle]');
    if (toggle) toggle.hidden = false;
    const saved = sessionStorage.getItem(SUBS_KEY);
    this.#setSubs(saved && found.some((sub) => sub.lang === saved) ? saved : 'off', false);
  }

  // Tries name.vtt, then name.srt (converted to WebVTT). Returns the text or null.
  async #loadSub(sub) {
    for (const [url, isSrt] of [[sub.vtt, false], [sub.srt, true]]) {
      if (!url) continue;
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const text = await response.text();
        if (!text.trim()) continue;
        return isSrt ? `WEBVTT\n\n${text.replace(/\r/g, '').replace(/(\d\d:\d\d:\d\d),(\d\d\d)/g, '$1.$2')}` : text;
      } catch {
        // Missing or blocked file: try the next one.
      }
    }
    return null;
  }

  #setSubs(value, remember = true) {
    for (const track of this.#video.textTracks) {
      track.mode = track.language === value ? 'showing' : 'disabled';
    }
    this.querySelectorAll('[command="--set-subs"]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === value));
    });
    this.querySelector('[data-subs-toggle]')?.setAttribute('aria-pressed', String(value !== 'off'));
    if (remember) sessionStorage.setItem(SUBS_KEY, value);
  }
}

if (!customElements.get('s-player-menu')) customElements.define('s-player-menu', SPlayerMenu);
